import {
  CalendarOutlined,
  RobotOutlined,
  ShareAltOutlined,
  SoundOutlined,
  UserOutlined
} from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
  message
} from 'antd';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import parse from 'html-react-parser';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import axios from '../../../helpers/axiosConfig';
import { getAllPetitionImages } from '../../../helpers/petitionImages';
import { checkUserLogin, isUserVerified } from '../../../helpers/functions';
import { deleteVote, getOnePetition, votePetition } from '../../../store/petition/petitionActions';
import { clearOnePetitionState } from '../../../store/petition/petitionSlice';
import ModerationStatus from '../../moderation/ModerationStatus/ModerationStatus';
import ImageGallery from '../../ui/ImageGallery/ImageGallery';
import TextToSpeech from '../../voice/TextToSpeech/TextToSpeech';
import './EnhancedPetitionDetails.css';

const { Title, Paragraph, Text } = Typography;

const EnhancedPetitionDetails = () => {
  const { t } = useTranslation('global');
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true'; // Проверяем, это предпросмотр?
  
  const { loading, onePetition } = useSelector((state) => state.petitions);
  const { user: currentUser } = useSelector((state) => state.account);
  const { status } = useSelector((state) => state.petitions);
  const [petition, setPetition] = useState(null);
  
  // Состояния для голосования
  const [votedFor, setVotedFor] = useState(false);
  const [votedAgainst, setVotedAgainst] = useState(false);
  const [isButtonDisabled, setisButtonDisabled] = useState(false);

  useEffect(() => {
    dispatch(getOnePetition({ id }));
    
    return () => dispatch(clearOnePetitionState());
  }, [id, dispatch]);

  useEffect(() => {
    if (onePetition) {
      setPetition(onePetition);
      
      // Проверяем, голосовал ли пользователь
      if (onePetition.choice === "True" || onePetition.choice === "False") {
        setisButtonDisabled(true);
      }
    }
  }, [onePetition]);

  // Автообновление инициативы если она на модерации (БЕЗ перезагрузки страницы)
  useEffect(() => {
    // Останавливаем auto-refresh если:
    // 1. Инициатива НЕ на модерации
    // 2. Инициатива заблокирована (is_blocked=true)
    const isBlocked = petition?.moderation_issues?.is_blocked;
    
    if (petition?.status === 'На модерации' && !isBlocked) {
      
      const interval = setInterval(async () => {
        try {
          const response = await axios.get(`/petition/${id}/`);
          
          // Обновляем только если статус изменился или есть новые данные
          if (response.data.status !== petition.status || 
              response.data.ai_moderation_result !== petition.ai_moderation_result) {
            setPetition(response.data);
            
            // Если инициатива опубликована, показываем уведомление
            if (response.data.status === 'Опубликована') {
              message.success({
                content: `🎉 ${t('moderation.petitionPublished')}`,
                duration: 5
              });
            }
            
            // Если инициатива заблокирована, показываем предупреждение
            if (response.data.moderation_issues?.is_blocked) {
              message.warning({
                content: `⚠️ ${t('moderation.petitionBlocked')}`,
                duration: 5
              });
            }
          } else {
          }
        } catch (error) {
          console.error('Ошибка обновления статуса:', error);
        }
      }, 10000); // Каждые 10 секунд

      return () => {
        clearInterval(interval);
      };
    } else if (isBlocked) {
    }
  }, [petition?.status, petition?.ai_moderation_result, petition?.moderation_issues?.is_blocked, id]);

  const handleShare = () => {
    const url = window.location.href;
    const shareData = {
      title: petition.title,
      text: `Поддержите инициативу: ${petition.title}`,
      url: url
    };

    if (navigator.share) {
      navigator.share(shareData).catch(console.error);
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        message.success('Ссылка скопирована в буфер обмена');
      }).catch(() => {
        message.error('Не удалось скопировать ссылку');
      });
    }
  };

  // Функции голосования
  const checkMaxVotes = () => {
    return petition?.support_votes < 10000;
  };

  const handleVoteFor = () => {
    if (!votedFor) {
      dispatch(votePetition({ onePetition: petition, choice: true }));
      setVotedFor(true);
      setisButtonDisabled(true);
      message.success('Ваш голос учтен');
    }
  };

  const handleVoteAgainst = () => {
    if (!votedAgainst) {
      dispatch(votePetition({ onePetition: petition, choice: "false" }));
      setVotedAgainst(true);
      setisButtonDisabled(true);
      message.success('Ваш голос учтен');
    }
  };

  const handleDeleteVote = () => {
    dispatch(deleteVote({ onePetition: petition }));
    setisButtonDisabled(false);
    setVotedFor(false);
    setVotedAgainst(false);
    message.info('Голос удален');
  };

  const formatDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'd MMMM yyyy', { locale: ru });
    } catch {
      return 'Дата не указана';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Опубликована': return 'green';
      case 'На модерации': return 'orange';
      case 'Завершенная': return 'blue';
      case 'Удалена': return 'red';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'Опубликована': return t('status.active');
      case 'На модерации': return t('status.moderation');
      case 'Завершенная': return t('status.completed');
      case 'Удалена': return t('status.deleted');
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="enhanced-petition-details loading">
        <Spin size="large" />
        <p>Загрузка инициативы...</p>
      </div>
    );
  }

  if (!petition) {
    return (
      <div className="enhanced-petition-details error">
        <Alert
          message={t('petition.notFound')}
          description="Запрашиваемая инициатива не существует или была удалена."
          type="error"
          showIcon
          action={
            <Button onClick={() => navigate('/petitions')}>
              К списку инициатив
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="enhanced-petition-details">
      <div className="container">
        
        {/* Заголовок и мета-информация */}
        <Card className="petition-header-card">
          <div className="petition-meta">
            <Space wrap>
              <Tag color={getStatusColor(petition.status)}>
                {getStatusText(petition.status)}
              </Tag>
              
              {petition.is_generated_by_ai && (
                <Tag icon={<RobotOutlined />} color="blue">
                  Сгенерировано ИИ
                </Tag>
              )}
              
              <Space size="small">
                <CalendarOutlined />
                <Text type="secondary">{formatDate(petition.date)}</Text>
              </Space>
              
              <Space size="small">
                <UserOutlined />
                <Text type="secondary">
                  {(() => {
                    // Проверяем, является ли это анонимным пользователем
                    const isAnonymous = !petition.author || 
                      petition.author.email === 'voice_petitions@system.local' ||
                      (petition.author.first_name === 'Голосовой' && petition.author.last_name === 'Помощник') ||
                      petition.client_id;
                    
                    if (isAnonymous) {
                      return 'Аноним';
                    }
                    
                    // Показываем обфусцированное имя
                    const firstName = petition.author.first_name?.charAt(0) || '';
                    const lastName = petition.author.last_name?.charAt(0) || '';
                    return `${firstName}*** ${lastName}***`;
                  })()}
                </Text>
              </Space>
            </Space>
          </div>

          <Title level={1} className="petition-title">
            {petition.title}
          </Title>

          <div className="petition-actions">
            <Space wrap>
              <Button 
                icon={<ShareAltOutlined />} 
                onClick={handleShare}
              >
                Поделиться
              </Button>
            </Space>
          </div>
        </Card>

        {/* Статус модерации - показываем только если это НЕ предпросмотр */}
        {petition.status === 'На модерации' && !isPreview && (
          <ModerationStatus petition={petition} />
        )}

        <Row gutter={[24, 24]}>
          
          {/* Основное содержимое */}
          <Col xs={24} lg={16}>
            <Card title="Описание инициативы" className="petition-content-card">
              
              {/* TTS для контента */}
              <div className="content-controls">
                <TextToSpeech 
                  text={petition.description}
                  language="ru"
                />
              </div>

              <Divider />

              {/* Основной текст */}
              <div className="petition-content">
                {typeof petition.description === 'string' ? 
                  parse(petition.description) : 
                  petition.description
                }
              </div>

              {/* Множественные изображения */}
              {petition.images && petition.images.length > 0 && (
                <ImageGallery 
                  images={petition.images}
                  title="Изображения инициативы"
                />
              )}

              {/* Одиночное изображение (для совместимости со старыми инициативами) */}
              {petition.image && (!petition.images || petition.images.length === 0) && (() => {
                const normalizedImages = getAllPetitionImages(petition);
                const imageUrl = normalizedImages.length > 0 ? normalizedImages[0].image : petition.image;
                return (
                  <div className="petition-image">
                    <img 
                      src={imageUrl} 
                      alt={petition.title}
                      style={{ 
                        width: '100%', 
                        maxWidth: 600, 
                        borderRadius: 8,
                        marginTop: 24 
                      }}
                    />
                  </div>
                );
              })()}

              {/* Голосовой транскрипт если есть */}
              {petition.voice_transcript && (
                <Card 
                  title={
                    <Space>
                      <SoundOutlined />
                      Исходное голосовое сообщение
                    </Space>
                  }
                  size="small"
                  className="transcript-card"
                  style={{ marginTop: 24 }}
                >
                  <Paragraph 
                    italic 
                    style={{ 
                      backgroundColor: '#f6f8fa', 
                      padding: 16, 
                      borderRadius: 6,
                      borderLeft: '4px solid #1890ff'
                    }}
                  >
                    "{petition.voice_transcript}"
                  </Paragraph>
                  
                  <TextToSpeech 
                    text={petition.voice_transcript}
                    language="ru"
                    showControls={true}
                  />
                </Card>
              )}

              {/* Теги */}
              {petition.tags && petition.tags.length > 0 && (
                <div className="petition-tags" style={{ marginTop: 24 }}>
                  <Text strong>Теги: </Text>
                  <Space wrap>
                    {petition.tags.map((tag) => (
                      <Tag key={tag.id} color="processing">
                        {tag.name}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}
            </Card>
          </Col>

          {/* Сайдбар с голосованием */}
          <Col xs={24} lg={8}>
            {/* Блок голосования ЗА/ПРОТИВ */}
            <Card 
              className="petition-voting-card"
              style={{ marginTop: 16 }}
            >
              {/* Прогресс голосов */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
                <div>
                  <Title level={1} style={{ margin: 0, color: '#1890ff', fontSize: 48 }}>
                    {(petition?.support_votes || 0) + (petition?.oppose_votes || 0)}
                  </Title>
                  <Text style={{ fontSize: 16, color: '#1890ff' }}>Голосов</Text>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Title level={1} style={{ margin: 0, fontSize: 48 }}>
                    10 000
                  </Title>
                  <Text style={{ fontSize: 16 }}>Цель</Text>
                </div>
              </div>

              {/* Автор */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
                <Avatar icon={<UserOutlined />} />
                <Text>
                  {(() => {
                    // Проверяем, является ли это анонимным пользователем
                    const isAnonymous = !petition?.author || 
                      petition.author.email === 'voice_petitions@system.local' ||
                      (petition.author.first_name === 'Голосовой' && petition.author.last_name === 'Помощник') ||
                      petition.client_id;
                    
                    if (isAnonymous) {
                      return t('status.anonymous');
                    }
                    
                    // Показываем обфусцированное имя
                    const firstName = petition?.author?.first_name?.charAt(0) || '';
                    const lastName = petition?.author?.last_name?.charAt(0) || '';
                    return `${firstName}*** ${lastName}***`;
                  })()}
                </Text>
              </div>

              {/* Кнопки голосования */}
              {!checkUserLogin() && (
                <Alert
                  message={t('petition.authRequired')}
                  description={
                    <span>
                      Пожалуйста,{' '}
                      <Button type="link" onClick={() => navigate('/login')} style={{ padding: 0 }}>
                        войдите в систему
                      </Button>
                    </span>
                  }
                  type="info"
                  showIcon
                />
              )}

              {checkUserLogin() && !isUserVerified() && (
                <Alert
                  message="Необходима верификация"
                  description={
                    <span>
                      Для голосования пройдите{' '}
                      <Button type="link" onClick={() => navigate('/profile')} style={{ padding: 0 }}>
                        верификацию профиля
                      </Button>
                    </span>
                  }
                  type="warning"
                  showIcon
                />
              )}

              {checkUserLogin() && isUserVerified() && checkMaxVotes() && (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {/* Кнопка ЗА */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Button
                      type="primary"
                      size="large"
                      onClick={handleVoteFor}
                      disabled={isButtonDisabled}
                      style={{ 
                        flex: 1,
                        height: 56,
                        fontSize: 16,
                        fontWeight: 'bold',
                        backgroundColor: isButtonDisabled && votedFor ? '#52c41a' : '#1890ff',
                        borderColor: isButtonDisabled && votedFor ? '#52c41a' : '#1890ff',
                        borderRadius: 28
                      }}
                    >
                      Я ЗА ИНИЦИАТИВУ
                    </Button>
                    <Text style={{ fontSize: 16, color: '#8c8c8c', minWidth: 80 }}>
                      {petition?.support_votes || 0} голосов
                    </Text>
                  </div>

                  {/* Кнопка ПРОТИВ */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Button
                      danger
                      size="large"
                      onClick={handleVoteAgainst}
                      disabled={isButtonDisabled}
                      style={{ 
                        flex: 1,
                        height: 56,
                        fontSize: 16,
                        fontWeight: 'bold',
                        backgroundColor: isButtonDisabled && votedAgainst ? '#ff4d4f' : '#ff7875',
                        borderColor: isButtonDisabled && votedAgainst ? '#ff4d4f' : '#ff7875',
                        borderRadius: 28,
                        color: '#fff'
                      }}
                    >
                      Я ПРОТИВ ИНИЦИАТИВЫ
                    </Button>
                    <Text style={{ fontSize: 16, color: '#8c8c8c', minWidth: 80 }}>
                      {petition?.oppose_votes || 0} голосов
                    </Text>
                  </div>

                  {/* Кнопка отмены голоса */}
                  {isButtonDisabled && (
                    <Button
                      type="default"
                      size="small"
                      onClick={handleDeleteVote}
                      block
                      style={{ marginTop: 8 }}
                    >
                      Отменить голос
                    </Button>
                  )}
                </Space>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default EnhancedPetitionDetails;